import React, { useCallback, useEffect, useId, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  encerramentoApi,
  nomeConfere,
  type AndamentoDoEncerramento,
  type PreviaDoEncerramento,
} from "../../../services/encerramento";

interface EncerrarImpulsionamentoProps {
  profileId: string;
  nomeDaEmpresa: string;
}

const INTERVALO_DO_ANDAMENTO_MS = 10_000;

const percentual = (parte: number, total: number) => (total > 0 ? Math.round((parte / total) * 100) : 100);

export const EncerrarImpulsionamento: React.FC<EncerrarImpulsionamentoProps> = ({ profileId, nomeDaEmpresa }) => {
  const tituloId = useId();
  const nomeId = useId();
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [previa, setPrevia] = useState<PreviaDoEncerramento | null>(null);
  const [andamento, setAndamento] = useState<AndamentoDoEncerramento | null>(null);
  const [verAndamento, setVerAndamento] = useState(false);
  const [removerAcesso, setRemoverAcesso] = useState(true);
  const [nomeDigitado, setNomeDigitado] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const atual = await encerramentoApi.andamento(profileId);
      setAndamento(atual);
      if (atual?.status === "EM_ANDAMENTO") {
        setVerAndamento(true);
      } else {
        setVerAndamento(false);
        setPrevia(await encerramentoApi.previa(profileId));
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível carregar a prévia.");
    } finally {
      setCarregando(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (!aberto) return undefined;
    carregar();
    return undefined;
  }, [aberto, carregar]);

  const emAndamento = verAndamento && andamento?.status === "EM_ANDAMENTO";
  useEffect(() => {
    if (!aberto || !emAndamento) return undefined;
    const timer = window.setInterval(() => {
      encerramentoApi
        .andamento(profileId)
        .then((atual) => setAndamento(atual))
        .catch(() => undefined);
    }, INTERVALO_DO_ANDAMENTO_MS);
    return () => window.clearInterval(timer);
  }, [aberto, emAndamento, profileId]);

  const fechar = () => {
    setAberto(false);
    setNomeDigitado("");
    setErro(null);
  };

  const encerrar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const iniciado = await encerramentoApi.iniciar(profileId, nomeDigitado, removerAcesso);
      setAndamento(iniciado);
      setVerAndamento(true);
      setNomeDigitado("");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível começar o encerramento.");
    } finally {
      setEnviando(false);
    }
  };

  const cancelar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      setAndamento(await encerramentoApi.cancelar(profileId));
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível cancelar.");
    } finally {
      setEnviando(false);
    }
  };

  const podeEncerrar = !!previa?.habilitado && nomeConfere(nomeDigitado, nomeDaEmpresa) && !enviando;

  return (
    <>
      <BotaoDeEncerrar type="button" onClick={() => setAberto(true)}>
        <span className="material-icons" aria-hidden="true">block</span>
        Encerrar impulsionamento
      </BotaoDeEncerrar>

      {aberto && (
        <Fundo onClick={(e) => e.target === e.currentTarget && fechar()}>
          <Caixa role="dialog" aria-modal="true" aria-labelledby={tituloId}>
            <Cabecalho>
              <h2 id={tituloId}>Encerrar impulsionamento</h2>
              <Fechar type="button" onClick={fechar} aria-label="Fechar">
                <span className="material-icons" aria-hidden="true">close</span>
              </Fechar>
            </Cabecalho>

            {carregando && <Apoio>Carregando…</Apoio>}
            {erro && <Erro role="alert">{erro}</Erro>}

            {!carregando && !verAndamento && previa && (
              <>
                <Apoio>Serão removidos de <strong>{previa.perfilNome}</strong> no Google:</Apoio>
                <Lista>
                  {previa.resumo.map((linha) => (
                    <li key={linha}>{linha}</li>
                  ))}
                </Lista>
                {previa.manuais.length > 0 && (
                  <Manual>
                    {previa.manuais.map((item) => (
                      <div key={item.texto}>
                        {item.texto}
                        {item.link && (
                          <>
                            {" · "}
                            <a href={item.link} target="_blank" rel="noreferrer">abrir a ficha</a>
                          </>
                        )}
                      </div>
                    ))}
                  </Manual>
                )}
                {andamento && (
                  <Apoio>
                    Último encerramento: {andamento.status}
                    {andamento.concluidoEm ? ` em ${dayjs(andamento.concluidoEm).format("DD/MM/YYYY HH:mm")}` : ""} ·{" "}
                    <LinkBotao type="button" onClick={() => setVerAndamento(true)}>ver relatório</LinkBotao>
                  </Apoio>
                )}

                <Marcar>
                  <input
                    type="checkbox"
                    checked={removerAcesso}
                    onChange={(e) => setRemoverAcesso(e.target.checked)}
                  />
                  Remover nosso acesso de gestor ao final
                </Marcar>

                <Aviso role="note">Isto apaga no Google e não tem volta.</Aviso>
                {!previa.habilitado && (
                  <Erro>
                    O encerramento está desligado no servidor. A prévia funciona, mas nada é apagado até a equipe
                    técnica ligar (ENCERRAMENTO_ENABLED).
                  </Erro>
                )}

                <label htmlFor={nomeId}>
                  <Apoio as="span">
                    Para confirmar, digite o nome da empresa: <strong>{nomeDaEmpresa}</strong>
                  </Apoio>
                </label>
                <CampoNome
                  id={nomeId}
                  value={nomeDigitado}
                  onChange={(e) => setNomeDigitado(e.target.value)}
                  autoComplete="off"
                  placeholder={nomeDaEmpresa}
                />

                <Acoes>
                  <BotaoNeutro type="button" onClick={fechar}>Voltar</BotaoNeutro>
                  <BotaoPerigo type="button" disabled={!podeEncerrar} onClick={encerrar}>
                    {enviando ? "Começando…" : "Encerrar e apagar tudo"}
                  </BotaoPerigo>
                </Acoes>
              </>
            )}

            {!carregando && verAndamento && andamento && (
              <Andamento
                andamento={andamento}
                enviando={enviando}
                onCancelar={cancelar}
                onVoltar={andamento.status === "EM_ANDAMENTO" ? undefined : () => void carregar()}
              />
            )}
          </Caixa>
        </Fundo>
      )}
    </>
  );
};

const NOMES_DO_STATUS: Record<AndamentoDoEncerramento["status"], string> = {
  PREVIA: "Prévia",
  EM_ANDAMENTO: "Em andamento",
  CONCLUIDO: "Concluído",
  CANCELADO: "Cancelado",
  FALHOU: "Falhou",
};

const Andamento: React.FC<{
  andamento: AndamentoDoEncerramento;
  enviando: boolean;
  onCancelar: () => void;
  onVoltar?: () => void;
}> = ({ andamento, enviando, onCancelar, onVoltar }) => {
  const resolvidos = andamento.feitos + andamento.erros + andamento.pulados;
  return (
    <>
      <Apoio>
        <strong>{NOMES_DO_STATUS[andamento.status]}</strong> · {resolvidos} de {andamento.total} itens
        {andamento.erros > 0 ? ` · ${andamento.erros} com erro` : ""}
      </Apoio>
      <Barra aria-label="Andamento geral" role="progressbar" aria-valuenow={percentual(resolvidos, andamento.total)}
        aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${percentual(resolvidos, andamento.total)}%` }} />
      </Barra>
      <Tipos>
        {andamento.porTipo.map((tipo) => {
          const feitosDoTipo = tipo.feitos + tipo.erros + tipo.pulados;
          return (
            <li key={tipo.tipo}>
              <div>
                <span>{tipo.rotulo}</span>
                <small>
                  {feitosDoTipo}/{tipo.total}
                  {tipo.erros > 0 ? ` · ${tipo.erros} erro(s)` : ""}
                </small>
              </div>
              <Barra $erro={tipo.erros > 0}>
                <span style={{ width: `${percentual(feitosDoTipo, tipo.total)}%` }} />
              </Barra>
            </li>
          );
        })}
      </Tipos>
      {andamento.ultimosErros.length > 0 && (
        <>
          <Apoio>Erros:</Apoio>
          <Lista>
            {andamento.ultimosErros.map((item, i) => (
              <li key={`${item.tipo}-${item.referencia}-${i}`}>
                {item.tipo}: {item.texto}
              </li>
            ))}
          </Lista>
        </>
      )}
      {andamento.avisos.length > 0 && (
        <Manual>
          {andamento.avisos.map((item, i) => (
            <div key={`${item.tipo}-${i}`}>
              {item.texto}
              {item.link && (
                <>
                  {" · "}
                  <a href={item.link} target="_blank" rel="noreferrer">abrir a ficha</a>
                </>
              )}
            </div>
          ))}
        </Manual>
      )}
      <Acoes>
        {onVoltar && (
          <BotaoNeutro type="button" onClick={onVoltar}>Ver prévia</BotaoNeutro>
        )}
        {andamento.status === "EM_ANDAMENTO" && (
          <BotaoPerigo type="button" disabled={enviando} onClick={onCancelar}>
            Cancelar o que falta
          </BotaoPerigo>
        )}
      </Acoes>
      {andamento.status === "EM_ANDAMENTO" && (
        <Apoio>O que já foi apagado não volta. Esta tela se atualiza sozinha.</Apoio>
      )}
    </>
  );
};

const BotaoDeEncerrar = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: #b3261e;
  font-family: var(--font-sans);
  font-size: 13px;
  cursor: pointer;
  border-radius: 10px;
  text-align: left;
  white-space: nowrap;
  flex-shrink: 0;

  .material-icons {
    font-size: 18px;
  }

  &:hover {
    background: #fce8e6;
  }

  @media (max-width: 1024px) {
    margin-top: 0;
  }
`;

const Fundo = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(32, 33, 36, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
  padding: 16px;
`;

const Caixa = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: 20px 24px 24px;
  font-family: var(--font-sans);
  color: var(--text, #3c4043);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const Cabecalho = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  h2 {
    margin: 0;
    font-size: 18px;
    color: #b3261e;
  }
`;

const Fechar = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  padding: 4px;
  border-radius: 8px;
`;

const Apoio = styled.p`
  margin: 12px 0 6px;
  font-size: 14px;
`;

const Lista = styled.ul`
  margin: 0 0 8px;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.6;
`;

const Manual = styled.div`
  margin: 8px 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fef7e0;
  color: #5f4b00;
  font-size: 13px;
  line-height: 1.5;
`;

const Marcar = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 4px;
  font-size: 14px;
  cursor: pointer;
`;

const Aviso = styled.p`
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fce8e6;
  color: #b3261e;
  font-weight: 600;
  font-size: 14px;
`;

const Erro = styled.p`
  margin: 8px 0;
  color: #b3261e;
  font-size: 13px;
`;

const CampoNome = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dadce0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
`;

const Acoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const BotaoNeutro = styled.button`
  height: 40px;
  padding: 0 16px;
  border: 1px solid #dadce0;
  background: #ffffff;
  border-radius: 20px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
`;

const BotaoPerigo = styled.button`
  height: 40px;
  padding: 0 16px;
  border: none;
  background: #b3261e;
  color: #ffffff;
  border-radius: 20px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;

  &:disabled {
    background: #f1b5b0;
    cursor: not-allowed;
  }
`;

const LinkBotao = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: var(--brand-700, #1a73e8);
  cursor: pointer;
  font: inherit;
  text-decoration: underline;
`;

const Barra = styled.div<{ $erro?: boolean }>`
  height: 8px;
  border-radius: 4px;
  background: #e8eaed;
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background: ${(p) => (p.$erro ? "#f9ab00" : "#1e8e3e")};
    transition: width 0.3s;
  }
`;

const Tipos = styled.ul`
  list-style: none;
  margin: 12px 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;

  li > div {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
  }
`;
